import React, {useContext, useEffect, useState} from 'react';
import {
    PLIConfigTemplateSection,
    PLISectionEditMode, SaveSectionNamePayload
} from 'src/models/calculation-builder/pli-config/PLIConfigTemplate';
import {Button, Header, Input, SpaceBetween} from '@amzn/awsui-components-react';
import PLITemplateSectionGrid from './PLITemplateSectionGrid';
import {PLIConfigContext} from './PLIConfigContainer';
import {ACTIONS} from 'src/services/calculation-builder/PLIConfigReducer';
import FormulaExpressionToolbar from 'src/components/shared/FormulaExpressionToolbar';
import {ALPHANUMERIC_SPACE_REGEX} from './Constants';
import { getPermissions } from 'src/components/AppPermissions';
import { AppModules } from 'src/models/permissions/RolePermissions';

export default function PLITemplateSection(props: {
    section: PLIConfigTemplateSection,
    isCustomPLI?: boolean,
    reloadPLIConfig: () => void,
    onError?: (error: string | undefined) => void
}) {
    const {section, reloadPLIConfig, onError} = props;
    const {state, dispatch, services} = useContext(PLIConfigContext);
    const { canEdit } = getPermissions(AppModules.CALCULATION_BUILDER);
    const [isEditing, setIsEditing] = useState(false);
    const [headerText, setHeaderText] = useState(section.name);
    const [savePayload, setSavePayload] = useState<SaveSectionNamePayload | null>(null);
    const [saveResult, isSaving, saveError] = services.pliConfigService.saveOverviewSectionPLIConfigStepName(savePayload);

    const onAddCalcStepClicked = () => {
        const newStep = services.pliConfigService.createNewStep(section, section.steps.length + 1);
        dispatch(ACTIONS.ADD_SECTION_STEP.withPayload({section, newStep}))
    }

    const onSaveSectionName = () => {
        if (!state.pliConfigMetadata?.calculationNumber) return;

        setSavePayload({
            calculationNumber: state.pliConfigMetadata?.calculationNumber,
            oldStepDescription: section.name,
            newStepDescription: headerText
        });
    }

    useEffect(() => {
        if (saveResult) {
            reloadPLIConfig();
            setSavePayload(null);
        }
    }, [saveResult]);

    useEffect(() => {
        if (saveError) {
            onError?.(saveError);
            setSavePayload(null);
        }
    }, [saveError]);

    const renderHeader = () => {
        if (!props.isCustomPLI) {
            return <Header variant='h3'>{section.name}</Header>;
        }

        if (isEditing) {
            return (
                <SpaceBetween direction="horizontal" size="xs">
                    <Input
                        value={headerText}
                        onChange={({detail}) => {
                            const value = detail.value.replace(ALPHANUMERIC_SPACE_REGEX, '');
                            setHeaderText(value);
                        }}
                    />
                    <Button variant={'icon'} iconName={'check'} onClick={onSaveSectionName}/>
                    <Button variant={'icon'} iconName={'close'} onClick={() => {
                        setHeaderText(section.name);
                        setIsEditing(false);
                    }}/>
                </SpaceBetween>
            );
        }

        return (
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <Header variant="h3">{headerText}</Header>
                {canEdit && (
                    <Button variant={'icon'} iconName={'edit'} onClick={() => setIsEditing(true)}>Edit Section Name</Button>
                )}
            </SpaceBetween>
        );
    };

    return <SpaceBetween direction='vertical' size='m'>
        {!section.isOverview &&
            <>
                {renderHeader()}
                {canEdit && PLISectionEditMode.EDITABLE === section.editMode &&
                    <FormulaExpressionToolbar builtInFunctions={["safeDivide()"]} dispatch={dispatch}/>}
            </>}
        <PLITemplateSectionGrid section={section}/>
        {canEdit && !section.isOverview && <Button variant="link" onClick={onAddCalcStepClicked}>Add calc step</Button>}
    </SpaceBetween>;
}